import { useEffect, useState, createContext, useContext } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import Lenis from "lenis";
import { Toaster } from "sonner";
import Home from "@/pages/Home";
import Blog from "@/pages/Blog";
import BlogPost from "@/pages/BlogPost";
import Book from "@/pages/Book";
import AdminLogin from "@/pages/AdminLogin";
import AdminDashboard from "@/pages/AdminDashboard";
import { fetchContent } from "@/lib/api";

const ContentContext = createContext(null);
export const useContent = () => useContext(ContentContext);

function LenisRoot({ children }) {
  useEffect(() => {
    const lenis = new Lenis({ lerp: 0.09, smoothWheel: true });
    let rafId;
    const raf = (t) => { lenis.raf(t); rafId = requestAnimationFrame(raf); };
    rafId = requestAnimationFrame(raf);
    return () => { cancelAnimationFrame(rafId); lenis.destroy(); };
  }, []);
  return children;
}

function ScrollToHash() {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (hash) {
      setTimeout(() => document.querySelector(hash)?.scrollIntoView({ behavior: "smooth" }), 150);
    } else {
      window.scrollTo(0, 0);
    }
  }, [pathname, hash]);
  return null;
}

function AnalyticsScripts({ content }) {
  useEffect(() => {
    const ga = content?.settings?.ga4_id;
    if (ga && !document.getElementById("ga4-script")) {
      const s = document.createElement("script");
      s.id = "ga4-script";
      s.async = true;
      s.src = `https://www.googletagmanager.com/gtag/js?id=${ga}`;
      document.head.appendChild(s);
      const inline = document.createElement("script");
      inline.id = "ga4-inline";
      inline.text = `window.dataLayer=window.dataLayer||[];function gtag(){dataLayer.push(arguments)}gtag('js',new Date());gtag('config','${ga}');`;
      document.head.appendChild(inline);
    }
    const px = content?.settings?.meta_pixel_id;
    if (px && !document.getElementById("meta-pixel")) {
      const s = document.createElement("script");
      s.id = "meta-pixel";
      s.text = `!function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){n.callMethod?n.callMethod.apply(n,arguments):n.queue.push(arguments)};if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';n.queue=[];t=b.createElement(e);t.async=!0;t.src=v;s=b.getElementsByTagName(e)[0];s.parentNode.insertBefore(t,s)}(window,document,'script','https://connect.facebook.net/en_US/fbevents.js');fbq('init','${px}');fbq('track','PageView');`;
      document.head.appendChild(s);
    }
  }, [content]);
  return null;
}

export default function App() {
  const [content, setContent] = useState(null);
  useEffect(() => {
    fetchContent().then(setContent).catch(() => setContent({}));
  }, []);

  return (
    <ContentContext.Provider value={content}>
      <BrowserRouter>
        <LenisRoot>
          <ScrollToHash />
          <AnalyticsScripts content={content} />
          <div className="noise-overlay" aria-hidden="true" />
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/blog" element={<Blog />} />
            <Route path="/blog/:slug" element={<BlogPost />} />
            <Route path="/book" element={<Book />} />
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/admin" element={<AdminDashboard />} />
          </Routes>
          <Toaster theme="dark" position="bottom-right" toastOptions={{ style: { background: "#1A1A1E", border: "1px solid rgba(200,125,85,0.3)", color: "#F7F4EF" } }} />
        </LenisRoot>
      </BrowserRouter>
    </ContentContext.Provider>
  );
}
